import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ArrowRight, Sparkles, History as HistoryIcon, TrendingUp, Cpu } from "lucide-react";

export default function StudentDashboard() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [ov, setOv] = useState(null);

    useEffect(() => { api.get("/student/overview").then((r) => setOv(r.data)); }, []);

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <span className="overline text-primary">▮ Dashboard</span>
                    <h1 className="mt-2 font-heading font-black text-4xl sm:text-5xl tracking-tighter">
                        {user?.full_name?.split(" ")[0]}, ready to <span className="text-primary">grind?</span>
                    </h1>
                </div>
                <Button data-testid="dashboard-generate-btn" onClick={() => nav("/generate")} className="rounded-sm bg-primary hover:bg-primary/90 btn-press h-11 px-5">
                    <Sparkles className="w-4 h-4 mr-2" /> Generate a new set <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
            </div>

            <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-4 stagger-in">
                <StatCard label="Sessions started" value={ov?.total_sessions ?? "—"} />
                <StatCard label="Sessions submitted" value={ov?.submitted_sessions ?? "—"} />
                <StatCard label="Avg % (last 5)" value={ov?.avg_percentage_last_5 != null ? `${ov.avg_percentage_last_5}%` : "—"} accent />
                <StatCard label="AI reports used" value={`${ov?.ai_analytics_used ?? 0}/${ov?.free_ai_analytics_limit ?? 15}`} />
            </div>

            <div className="mt-10 grid lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 border border-border rounded-sm p-6">
                    <div className="flex items-center justify-between">
                        <h2 className="font-heading font-bold text-xl">Recent attempts</h2>
                        <Link to="/history" className="text-xs overline text-muted-foreground hover:text-foreground">See all →</Link>
                    </div>
                    <div className="mt-4">
                        {(!ov?.recent_attempts || ov.recent_attempts.length === 0) && (
                            <div className="border border-dashed border-border rounded-sm p-8 text-center text-sm text-muted-foreground">
                                No attempts yet. <Link className="text-primary underline underline-offset-4" to="/generate">Generate your first set →</Link>
                            </div>
                        )}
                        <div className="divide-y divide-border">
                            {(ov?.recent_attempts || []).map((a) => (
                                <Link to={`/result/${a.session_id}`} key={a.session_id} data-testid={`recent-attempt-${a.session_id}`}
                                    className="flex items-center justify-between py-3 hover:bg-accent/40 px-2 -mx-2 rounded-sm transition-colors">
                                    <div>
                                        <div className="font-mono text-sm">{a.exam} · {a.total_questions}Q · {new Date(a.submitted_at).toLocaleDateString()}</div>
                                        <div className="text-xs text-muted-foreground mt-0.5">
                                            {a.correct}✓ {a.incorrect}✗ {a.unanswered}○
                                        </div>
                                    </div>
                                    <div className="font-mono font-bold text-lg">
                                        {a.percentage}<span className="text-xs text-muted-foreground">%</span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="border border-border rounded-sm p-6">
                        <span className="overline text-muted-foreground">Free tier usage</span>
                        <div className="mt-3">
                            <div className="text-xs text-muted-foreground mb-1 flex justify-between">
                                <span>Generations</span>
                                <span className="font-mono">{ov?.free_generations_used ?? 0} / {ov?.free_generation_limit ?? 15}</span>
                            </div>
                            <Progress value={Math.min(100, ((ov?.free_generations_used ?? 0) / (ov?.free_generation_limit ?? 15)) * 100)} className="h-1 rounded-sm" />
                            <p className="text-xs text-muted-foreground mt-2">After the free tier, generation stays free forever — only AI analytics needs subscription.</p>
                        </div>
                        <div className="mt-4">
                            <div className="text-xs text-muted-foreground mb-1 flex justify-between">
                                <span>AI analytics</span>
                                <span className="font-mono">{ov?.ai_analytics_used ?? 0} / {ov?.free_ai_analytics_limit ?? 15}</span>
                            </div>
                            <Progress value={Math.min(100, ((ov?.ai_analytics_used ?? 0) / (ov?.free_ai_analytics_limit ?? 15)) * 100)} className="h-1 rounded-sm" />
                            {!ov?.is_subscribed && (
                                <Link to="/subscription">
                                    <Button data-testid="dashboard-upgrade-btn" size="sm" variant="outline" className="w-full mt-4 rounded-sm btn-press">
                                        <TrendingUp className="w-4 h-4 mr-2" /> Upgrade to Plus
                                    </Button>
                                </Link>
                            )}
                        </div>
                    </div>

                    <Link to="/desktop">
                        <div className="border border-border rounded-sm p-6 hover:border-primary transition-colors cursor-pointer">
                            <Cpu className="w-6 h-6 text-primary" />
                            <h3 className="mt-3 font-heading font-bold">Contribute compute</h3>
                            <p className="text-xs text-muted-foreground mt-1">Run the desktop worker while you sleep. Help other aspirants and unlock Prepcheck perks.</p>
                        </div>
                    </Link>
                </div>
            </div>
        </div>
    );
}

const StatCard = ({ label, value, accent }) => (
    <div className="border border-border rounded-sm p-5">
        <div className="overline text-muted-foreground">{label}</div>
        <div className={`font-heading font-black text-3xl mt-2 ${accent ? "text-primary" : ""} font-mono`}>{value}</div>
    </div>
);
